import type { StorageKey } from './storageKeys';

/**
 * Reads the raw string stored under a key. Resolves window.localStorage on every call so a replaced storage (tests, private mode stubs) is honoured.
 *
 * @param key - Registered storage key to read.
 * @returns The stored string, or null when the key is absent.
 */
const get = (key: StorageKey) => window.localStorage.getItem(key);

/**
 * Writes a value under a key, coerced with String() exactly like assigning a property on localStorage. So `undefined` is stored as "undefined" and `null` as "null".
 *
 * @param key - Registered storage key to write.
 * @param value - Value to persist; stringified before it is written.
 * @returns Nothing.
 */
const set = (key: StorageKey, value: unknown) => {
  window.localStorage.setItem(key, String(value));
};

/**
 * Deletes a single key.
 *
 * @param key - Registered storage key to delete.
 * @returns Nothing.
 */
const remove = (key: StorageKey) => {
  window.localStorage.removeItem(key);
};

/**
 * Deletes every key held by the origin, not only the registered ones.
 *
 * @returns Nothing.
 */
const clear = () => {
  window.localStorage.clear();
};

/**
 * Reads and JSON-parses the value stored under a key. Yields the fallback when the key is absent, empty, unparseable, or when localStorage is unavailable.
 *
 * @param key - Registered storage key to read.
 * @param fallback - Value returned when nothing usable is stored.
 * @returns The parsed value, or the fallback.
 */
const getJson = <T>(key: StorageKey, fallback: T): T => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Serializes a value with JSON.stringify and stores it under a key.
 *
 * @param key - Registered storage key to write.
 * @param value - JSON-serializable value to persist.
 * @returns Nothing.
 */
const setJson = (key: StorageKey, value: unknown) => {
  window.localStorage.setItem(key, JSON.stringify(value));
};

/**
 * Empties localStorage but keeps the listed keys. Snapshots each preserved key first and writes back only those that were present.
 *
 * @param keysToKeep - Registered keys whose current values survive the clear.
 * @returns Nothing.
 */
const clearExcept = (keysToKeep: StorageKey[]) => {
  const kept = keysToKeep.map((key) => [key, get(key)] as const);
  clear();
  kept.forEach(([key, value]) => {
    if (value !== null) {
      window.localStorage.setItem(key, value);
    }
  });
};

const storage = { get, set, remove, clear, getJson, setJson, clearExcept };

export default storage;
